import {getSupabaseStorageKey} from '@/shared/api/supabase.ts';
import {Session, User} from '@supabase/supabase-js';

// ============================================================================
// OAuth Callback Detection
// ============================================================================

/**
 * Check if URL has successful OAuth callback tokens (not errors).
 * This happens after Google redirects back with auth tokens.
 */
export const hasOAuthSuccessCallback = (): boolean => {
    const hash = window.location.hash;
    return hash.includes('access_token=') && !hash.includes('error=');
};

/**
 * Check if URL has OAuth error callback in the hash.
 * This happens when Google OAuth fails or user denies access.
 */
export const hasOAuthErrorCallback = (): boolean => {
    const hash = window.location.hash;
    return hash.includes('error=');
};

// ============================================================================
// Session Storage Utilities
// ============================================================================

/**
 * Get session directly from localStorage (fast, sync).
 * Avoids calling supabase.auth.getSession() which can hang in some scenarios.
 * Returns full Session object for use in AuthContext.
 */
export const getStoredSession = (): Session | null => {
    try {
        const storageKey = getSupabaseStorageKey();
        const stored = localStorage.getItem(storageKey);

        if (!stored) {
            return null;
        }

        const parsed = JSON.parse(stored);

        // Check if session is expired
        const now = Math.floor(Date.now() / 1000);
        if (parsed.expires_at && parsed.expires_at <= now) {
            localStorage.removeItem(storageKey);
            return null;
        }

        return {
            access_token: parsed.access_token,
            refresh_token: parsed.refresh_token,
            token_type: parsed.token_type || 'bearer',
            expires_in: parsed.expires_at - now,
            expires_at: parsed.expires_at,
            user: parsed.user as User,
        };
    } catch {
        return null;
    }
};

/**
 * Check if there's a valid (non-expired) session in localStorage.
 * Lightweight check for route loaders that only need to know auth status.
 */
export const isSessionValid = (): boolean => {
    return getStoredSession() !== null;
};

/**
 * Clear stored session from localStorage.
 */
export const clearStoredSession = (): void => {
    try {
        const storageKey = getSupabaseStorageKey();
        localStorage.removeItem(storageKey);
    } catch {
        // Ignore errors
    }
};
